// CustomerQuoteScreen.tsx — 고객 제출용 정식 견적서(이슈 #110).
// 내부 견적상세(DetailScreen)와 분리된 외부 제출 문서. 모든 항목 수정·입력 가능 —
// 연동값은 견적 등록 데이터에서 프리필 후 override 가능, 직접 입력값은 customerDoc 영속.
// 출력은 window.print() + @media print(헤더밴드/표 헤더 매 페이지 반복, A4 portrait).

import { ErrorState, LoadingState } from "@/components/States";
import { Icon, Pill, Select, StatusBadge, TableScroll, TextInput, Textarea } from "@/components/ui";
import type { PartLabel } from "@/lib/cost/cncBreakdown";
import {
  type CustomerQuoteItem,
  autoRecipient,
  customerDocNo,
  customerQuoteItems,
  prefillCustomerDoc,
} from "@/lib/customerQuote";
import { type CustomerDoc, STATUS, type StatusKey, customerNameFrom, fmt } from "@/lib/data";
import {
  updateQuote,
  updateQuoteStatus,
  useCncLaborRates,
  useCncRawMaterials,
  useCompanySeal,
  useCustomers,
  useQuote,
} from "@/lib/hooks";
import { useQuoteImageSrc } from "@/lib/hooks";
import { sealFileError } from "@/lib/imageFile";
import { useIsMobile } from "@/lib/useViewport";
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";

// 한부금속 로고 — 고객 제공 CI 이미지(public/company-logo.png, 투명 PNG). 인쇄 호환 plain img.
const HanbuLogo = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <img
      src="/company-logo.png"
      alt="한부금속 로고"
      style={{ width: 40, height: 40, objectFit: "contain", flexShrink: 0 }}
    />
    <div style={{ lineHeight: 1.15 }}>
      <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 16 }}>한부금속</div>
      <div style={{ fontSize: 10.5, color: "var(--text-dim)", letterSpacing: "0.08em" }}>
        HANBU METAL
      </div>
    </div>
  </div>
);

// 회사 연락처 — 고정 문안(헤더 우측 표기, 고객 제공 값).
const COMPANY_ADDR = "주소 : 경기도 이천시 마장면 이장로 227-21";
const COMPANY_TEL = "TEL 031-798-9200-3   FAX 031-798-9205";

const th: CSSProperties = {
  padding: "8px 8px",
  fontWeight: 700,
  fontSize: 11,
  color: "var(--text-base)",
  border: "1px solid var(--bg-surface-3)",
  background: "var(--bg-card-hover)",
  whiteSpace: "nowrap",
};
const td: CSSProperties = {
  padding: "6px 8px",
  fontSize: 12,
  border: "1px solid var(--bg-surface-3)",
  verticalAlign: "middle",
};
// 품목 행 셀 — 이미지 썸네일 대응 여유 패딩(문서형 목업 기준).
const itd: CSSProperties = { ...td, padding: "10px 8px" };

// 품목 표 헤더 셀. 연동/직접입력 구분 태그는 문서(인쇄물)에 노출하지 않는다 —
// 연동값은 그냥 출력되고, 직접 입력 항목은 placeholder 로 입력 필요를 표시.
const ItemTh = ({ label, width }: { label: string; width?: number }) => (
  <th style={width ? { ...th, width } : th}>{label}</th>
);

// 페이지당 품목 행 수 — 매 페이지 헤더밴드+표 헤더 반복을 위해 청크로 분할(인쇄용).
// ponytail: 고정 행수 휴리스틱. 행 높이가 크게 달라지면 측정 기반 분할로 교체.
const ROWS_PER_PAGE = 8;

// 편집 인풋 + 인쇄용 값 텍스트 이중 렌더 — 화면에서는 입력 폼, 인쇄(PDF)에서는 입력 박스 없이
// 값만 출력한다(미입력 값은 빈칸 — placeholder 미출력). 긴 값은 셀 안에서 개행(pre-wrap/break-word)
// 되어 overflow 로 잘리지 않는다.
// multiline: 품명처럼 개행이 필요한 셀만 textarea 로(그 외는 기존 단행 인풋 유지).
const EditText = ({
  value,
  onChange,
  placeholder,
  align,
  mono,
  height = 30,
  multiline,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  align?: "left" | "right";
  mono?: boolean;
  height?: number;
  multiline?: boolean;
}) => (
  <>
    <div className="cq-edit">
      {multiline ? (
        <Textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={2}
          style={{ padding: "5px 12px", lineHeight: 1.4 }}
        />
      ) : (
        <TextInput
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          align={align}
          mono={mono}
          style={{ height }}
        />
      )}
    </div>
    <div
      className="cq-printval"
      style={{
        textAlign: align,
        fontFamily: mono ? "ui-monospace, monospace" : undefined,
      }}
    >
      {value}
    </div>
  </>
);

// 품목 이미지 — 스토리지 키(파트 이미지 연동)는 인증 blob URL 로, URL 직접 입력은 그대로 표시.
const ItemImage = ({ src }: { src: string }) => {
  const url = useQuoteImageSrc(src);
  if (!url) return null;
  return (
    <img
      key={url}
      src={url}
      alt=""
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
      style={{
        display: "block",
        maxHeight: 64,
        maxWidth: "100%",
        objectFit: "contain",
        margin: "0 auto 4px",
      }}
    />
  );
};

// 상단 정보 표의 라벨(th)+값(td) 셀 쌍 — 연동/직접입력 모두 편집 가능한 인풋(인쇄는 값만).
const InfoCell = ({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) => (
  <>
    <th style={{ ...th, width: 78, textAlign: "left" }}>{label}</th>
    <td style={td}>
      <EditText value={value} onChange={onChange} placeholder={hint} height={32} />
    </td>
  </>
);

export const CustomerQuoteScreen = ({
  quoteId,
  authorName,
  onToast,
  onBack,
}: {
  quoteId: string;
  // 작성자 연동값 — 로그인 사용자(견적서를 작성·출력하는 사람). 표지에서 덮어쓰면 override 보존.
  authorName: string;
  onToast: (msg: string) => void;
  onBack: () => void;
}) => {
  const mobile = useIsMobile();
  const quoteRes = useQuote(quoteId);
  const customersRes = useCustomers();
  const cncRaw = useCncRawMaterials();
  const cncLabor = useCncLaborRates();
  const sealRes = useCompanySeal();
  const sealInputRef = useRef<HTMLInputElement>(null);
  const base = quoteRes.data;

  const [doc, setDoc] = useState<CustomerDoc>({
    recipient: "",
    reference: "",
    remark: "",
    items: [],
    overrides: {},
  });
  const [pending, setPending] = useState(false);

  // 연동 품목 — 마스터에서 재질/공법 라벨 resolve 후 customerQuoteItems 로 평면화.
  const linkedItems: CustomerQuoteItem[] = useMemo(() => {
    if (!base) return [];
    const labels: PartLabel[] = base.cnc.map((p) => ({
      material: cncRaw.data.find((m) => m.id === p.selection.materialId)?.name ?? "",
      method: cncLabor.data.find((l) => l.id === p.selection.rows[0]?.laborRateId)?.process ?? "",
    }));
    return customerQuoteItems(base.cnc, labels);
  }, [base, cncRaw.data, cncLabor.data]);

  // 프리필 — 견적을 처음 열 때만 저장된 customerDoc 으로 초기화. 같은 견적을 다시 조회하면
  // (상태 변경 후 reload, 마스터 로드 완료 등) 편집 중 입력값을 유지한다 — 재조회마다
  // 초기화하면 저장 전 입력이 전부 날아가 다시 입력해야 했다.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!base) return;
    const first = loadedFor.current !== base.id;
    loadedFor.current = base.id;
    setDoc((prev) =>
      prefillCustomerDoc({
        saved: base.customerDoc,
        quoteRemark: base.remark ?? "",
        itemCount: linkedItems.length,
        editing: first ? undefined : prev,
      })
    );
  }, [base, linkedItems]);

  if (quoteRes.loading)
    return (
      <div style={{ padding: "24px 28px" }}>
        <LoadingState />
      </div>
    );
  if (quoteRes.error)
    return (
      <div style={{ padding: "24px 28px" }}>
        <ErrorState error={quoteRes.error} onRetry={() => void quoteRes.reload()} />
      </div>
    );
  if (!base)
    return (
      <div style={{ padding: "72px 28px", textAlign: "center", color: "var(--text-faint)" }}>
        견적을 찾을 수 없습니다
      </div>
    );

  const custName = customerNameFrom(customersRes.data, base.customerId);
  // 수신 — 고객사명 + 견적 담당자를 연동값으로 자동 채운다(입력하면 그 값이 우선).
  const recipient = doc.recipient || autoRecipient(custName, base.owner);
  // 연동값 + override(편집됨) — get/set 헬퍼. override 빈 문자열도 "지움" 의도로 보존.
  const ov = (key: string, linked: string): string => doc.overrides?.[key] ?? linked;
  const setOv = (key: string, v: string) =>
    setDoc((d) => ({ ...d, overrides: { ...d.overrides, [key]: v } }));
  const setItem = (i: number, patch: Partial<CustomerDoc["items"][number]>) =>
    setDoc((d) => ({ ...d, items: d.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));

  const docNo = ov("docNo", customerDocNo(base));
  const title = ov("title", base.project);
  const created = ov("created", base.created);
  // 작성자 — 로그인 사용자(견적의 담당자는 고객사 연락처 값이라 수신 쪽으로 간다).
  const owner = ov("owner", authorName || base.owner);

  const save = async () => {
    setPending(true);
    try {
      // 비고는 견적 등록 화면과 같은 값 — quote.remark 에도 기록해 양방향 연동.
      await updateQuote(base.id, { ...base, remark: doc.remark, customerDoc: doc });
      onToast("견적서 직접 입력 값을 저장했습니다");
    } catch (e) {
      onToast(e instanceof Error ? e.message : "저장에 실패했습니다");
    } finally {
      setPending(false);
    }
  };

  // 직인 업로드 — 검증 후 multipart 로 /company/seal 오브젝트 스토리지에 영구 저장(모든 견적서 공용).
  const uploadSeal = async (file: File | undefined) => {
    if (!file) return;
    const err = sealFileError(file);
    if (err) {
      onToast(err);
      return;
    }
    try {
      await sealRes.save(file);
      onToast("직인을 저장했습니다");
    } catch (e) {
      onToast(e instanceof Error ? e.message : "직인 저장에 실패했습니다");
    }
  };

  const print = () => {
    onToast("제출 견적 PDF — 인쇄 대화상자를 엽니다");
    window.print();
  };

  const changeStatus = async (v: string) => {
    setPending(true);
    try {
      await updateQuoteStatus(base.id, v as StatusKey);
      await quoteRes.reload();
      onToast(`상태가 '${STATUS[v as StatusKey].label}'(으)로 변경되었습니다`);
    } catch (e) {
      onToast(e instanceof Error ? e.message : "상태 변경에 실패했습니다");
    } finally {
      setPending(false);
    }
  };

  // 매 페이지 반복 헤더(로고 + 제목 + 회사 연락처) — 인쇄 분할 시 각 페이지 상단.
  // 견적번호는 상단 정보 표로 이동(목업 기준).
  // ※ PageHeader/ItemTable 은 렌더마다 재생성되는 함수라 JSX(<PageHeader/>)로 쓰면
  //   매 렌더 새 컴포넌트 타입 → 서브트리 리마운트 → 한글 IME 조합이 키 입력마다 끊긴다.
  //   plain 함수 호출({PageHeader()})로 렌더해 타입이 트리에 들어가지 않게 한다(내부 훅 없음 전제).
  const PageHeader = () => (
    <div className="cq-pagehead">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <HanbuLogo />
        <div
          style={{
            fontFamily: "var(--font-title)",
            fontWeight: 800,
            fontSize: 30,
            letterSpacing: "0.4em",
            paddingLeft: "0.4em",
          }}
        >
          견 적 서
        </div>
        <div
          style={{ fontSize: 10.5, color: "var(--text-dim)", textAlign: "right", lineHeight: 1.7 }}
        >
          <div>{COMPANY_ADDR}</div>
          <div>{COMPANY_TEL}</div>
        </div>
      </div>
    </div>
  );

  // 품목 표(헤더 + 행) — 인쇄 분할용으로 행 청크를 받아 페이지마다 렌더.
  const ItemTable = ({ from, to }: { from: number; to: number }) => (
    <TableScroll>
      <table
        className="cq-itemtable"
        style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}
      >
        <thead>
          <tr>
            <ItemTh label="No" width={32} />
            <ItemTh label="품명" />
            <ItemTh label="이미지" width={110} />
            <ItemTh label="공법" width={90} />
            <ItemTh label="견적가(원)" width={96} />
            <ItemTh label="재질" width={80} />
            <ItemTh label="도금" width={90} />
            <ItemTh label="연간수량(EA)" width={84} />
            <ItemTh label="기타" width={96} />
          </tr>
        </thead>
        <tbody>
          {linkedItems.slice(from, to).map((it, k) => {
            const i = from + k;
            const di = doc.items[i] ?? { image: "", etc: "" };
            // 이미지 = 직접 입력(URL/키) 우선, 없으면 견적등록 파트 이미지(data URL) 자동 연동.
            const imgSrc = di.image.trim() !== "" ? di.image : it.image;
            return (
              <tr key={`${docNo}-${i}`} className="cq-row">
                <td style={{ ...itd, textAlign: "center" }}>{i + 1}</td>
                <td style={itd}>
                  <EditText
                    value={ov(`item.${i}.name`, it.itemName)}
                    onChange={(v) => setOv(`item.${i}.name`, v)}
                    multiline
                  />
                </td>
                {/* 이미지 — 견적등록 파트 이미지 자동 연동(URL 직접 입력 폼 제거,
                    구 문서의 di.image 저장값은 계속 표시). */}
                <td style={itd}>{imgSrc !== "" && <ItemImage src={imgSrc} />}</td>
                <td style={itd}>
                  <EditText
                    value={ov(`item.${i}.method`, it.method)}
                    onChange={(v) => setOv(`item.${i}.method`, v)}
                  />
                </td>
                {/* 견적가 — 목업 기준 브랜드색 강조(.cq-price input/.cq-printval 규칙). */}
                <td style={itd} className="cq-price">
                  <EditText
                    value={ov(`item.${i}.price`, it.unitPrice ? fmt(it.unitPrice) : "")}
                    onChange={(v) => setOv(`item.${i}.price`, v)}
                    align="right"
                    mono
                  />
                </td>
                <td style={itd}>
                  <EditText
                    value={ov(`item.${i}.material`, it.material)}
                    onChange={(v) => setOv(`item.${i}.material`, v)}
                  />
                </td>
                <td style={itd}>
                  <EditText
                    value={ov(`item.${i}.plating`, it.plating)}
                    onChange={(v) => setOv(`item.${i}.plating`, v)}
                  />
                </td>
                <td style={itd}>
                  <EditText
                    value={ov(`item.${i}.qty`, it.annualQty ? fmt(it.annualQty) : "")}
                    onChange={(v) => setOv(`item.${i}.qty`, v)}
                    align="right"
                    mono
                  />
                </td>
                <td style={itd}>
                  <EditText value={di.etc} onChange={(v) => setItem(i, { etc: v })} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableScroll>
  );

  // 페이지 청크 경계 — 첫 페이지에 정보 밴드 + 첫 청크, 이후 페이지는 품목만.
  const pageStarts = linkedItems.length
    ? Array.from(
        { length: Math.ceil(linkedItems.length / ROWS_PER_PAGE) },
        (_, p) => p * ROWS_PER_PAGE
      )
    : [0];

  return (
    <div
      className="cq-root"
      style={{ padding: "20px 28px 56px", display: "flex", flexDirection: "column", gap: 16 }}
    >
      {/* 액션 바 (인쇄 숨김) */}
      <div
        className="cq-noprint"
        style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
      >
        <Pill variant="ghost" size="sm" icon={<Icon name="list" size={15} />} onClick={onBack}>
          견적 상세
        </Pill>
        <div style={{ flex: 1 }} />
        <StatusBadge status={base.status} />
        <Select
          value={base.status}
          onChange={pending ? undefined : (v) => void changeStatus(v)}
          options={(Object.keys(STATUS) as StatusKey[]).map((k) => ({
            value: k,
            label: STATUS[k].label,
          }))}
          style={{ width: 140, height: 38, opacity: pending ? 0.6 : 1 }}
        />
        <Pill
          variant="outlined"
          size="sm"
          disabled={pending}
          icon={<Icon name="save" size={15} />}
          onClick={() => void save()}
        >
          저장
        </Pill>
        <Pill variant="primary" size="sm" icon={<Icon name="file" size={15} />} onClick={print}>
          제출 견적 PDF
        </Pill>
      </div>
      <div className="cq-noprint" style={{ fontSize: 12, color: "var(--text-dim)" }}>
        ※ 모든 항목은 수정 및 입력이 가능합니다.
      </div>

      {/* 문서 영역 — 화면에서는 A4 시트 느낌의 중앙 카드(인쇄 시 제약 해제, print CSS 참조). */}
      <div
        className="cq-doc"
        style={{
          width: "100%",
          maxWidth: 1100,
          margin: "0 auto",
          background: "var(--bg-card)",
          border: "1px solid var(--bg-surface-3)",
          borderRadius: 12,
          boxShadow: "0 4px 18px rgba(0,0,0,0.10)",
          padding: mobile ? "20px 14px" : "36px 42px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* 페이지별 청크. 각 청크 = PageHeader + (첫 페이지만 정보 표) + 품목 표. */}
        {pageStarts.map((start, p) => (
          <div key={`page-${start}`} className="cq-page">
            {PageHeader()}
            {p === 0 && (
              <TableScroll>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                    margin: "16px 0",
                  }}
                >
                  <tbody>
                    <tr>
                      <InfoCell
                        label="수신"
                        value={recipient}
                        onChange={(v) => setDoc((d) => ({ ...d, recipient: v }))}
                        hint="수신처를 입력하세요"
                      />
                      <InfoCell
                        label="견적번호"
                        value={docNo}
                        onChange={(v) => setOv("docNo", v)}
                      />
                    </tr>
                    <tr>
                      <InfoCell label="제 목" value={title} onChange={(v) => setOv("title", v)} />
                      <InfoCell
                        label="작성일"
                        value={created}
                        onChange={(v) => setOv("created", v)}
                      />
                    </tr>
                    <tr>
                      <InfoCell
                        label="참 조"
                        value={doc.reference}
                        onChange={(v) => setDoc((d) => ({ ...d, reference: v }))}
                        hint="참조를 입력하세요"
                      />
                      <InfoCell label="작성자" value={owner} onChange={(v) => setOv("owner", v)} />
                    </tr>
                  </tbody>
                </table>
              </TableScroll>
            )}
            {/* 견적 품목 섹션 타이틀 — 매 페이지 표 상단(목업 기준). */}
            <div
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: "var(--brand)",
                margin: p === 0 ? "0 0 6px" : "14px 0 6px",
              }}
            >
              견적 품목
            </div>
            {ItemTable({ from: start, to: start + ROWS_PER_PAGE })}
            {/* 비고 + 대표이사/직인 — 마지막 페이지의 견적 품목 바로 아래(인쇄 시 같은 페이지,
              cq-bottom 으로 내부 분할 방지). 라벨/업로드 버튼은 헤더 행, 본문 행은 stretch 로
              비고 박스와 직인 박스의 상·하단을 나란히 맞춘다. */}
            {p === pageStarts.length - 1 && (
              <div className="cq-bottom" style={{ marginTop: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
                    비고
                  </div>
                  <div style={{ flex: 1 }} />
                  <div className="cq-noprint">
                    <input
                      ref={sealInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        void uploadSeal(e.target.files?.[0]);
                        e.target.value = ""; // 같은 파일 재선택도 change 발생하도록 초기화
                      }}
                    />
                    <Pill variant="ghost" size="sm" onClick={() => sealInputRef.current?.click()}>
                      직인 업로드
                    </Pill>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 24,
                    alignItems: "stretch",
                    flexWrap: mobile ? "wrap" : "nowrap",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: mobile ? "100%" : 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div className="cq-edit" style={{ flex: 1, display: "flex" }}>
                      <Textarea
                        value={doc.remark}
                        onChange={(v) => setDoc((d) => ({ ...d, remark: v }))}
                        rows={3}
                        placeholder="비고를 입력하세요"
                        style={{ resize: "none" }}
                      />
                    </div>
                    <div className="cq-printval">{doc.remark}</div>
                  </div>
                  {/* 직인 — /company/seal 에 영구 저장된 업로드 이미지(미설정 시 placeholder 원). */}
                  <div
                    style={{
                      width: mobile ? "100%" : 260,
                      border: "1px solid var(--bg-surface-3)",
                      padding: "14px 18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--font-title)",
                        fontWeight: 800,
                        fontSize: 18,
                        letterSpacing: "0.3em",
                      }}
                    >
                      대 표 이 사
                    </div>
                    {sealRes.data ? (
                      <img
                        src={sealRes.data}
                        alt="직인"
                        style={{ width: 78, height: 78, objectFit: "contain", flexShrink: 0 }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 78,
                          height: 78,
                          borderRadius: "50%",
                          border: "2px solid var(--text-negative)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--text-negative)",
                          fontSize: 11,
                          opacity: 0.8,
                          flexShrink: 0,
                        }}
                      >
                        (직인)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 인쇄 CSS — A4 portrait, thead 매 페이지 반복, 페이지 단위 break, 행 분리 방지.
        + 화면용: 견적가 인풋 브랜드색 강조(TextInput 내부 input 색 고정이라 !important).
        + 이중 렌더: 화면은 입력 폼(.cq-edit), 인쇄는 값 텍스트(.cq-printval) — 입력 박스/
          placeholder 는 PDF 에 노출되지 않고, 긴 값은 pre-wrap/break-word 로 개행된다. */}
      <style>
        {`.cq-price input,.cq-price .cq-printval{color:var(--brand)!important;font-weight:700}
        .cq-printval{display:none;font-size:12px;line-height:1.45;white-space:pre-wrap;word-break:break-word}
        @media print{
          @page{size:A4 portrait;margin:12mm}
          .cq-noprint{display:none!important}
          .cq-edit{display:none!important}
          .cq-printval{display:block!important;color:#111}
          nav,aside,header{display:none!important}
          .cq-root{padding:0!important;gap:0!important}
          body{background:#fff!important}
          .cq-doc{max-width:none!important;border:none!important;border-radius:0!important;box-shadow:none!important;padding:0!important;background:#fff!important}
          .cq-doc th,.cq-doc td{border-color:#ddd!important}
          .cq-doc th{background:#f3f3f3!important;color:#111!important}
          /* 품목 표 열 너비 — 화면용 px 폭(합계 678px)은 A4 가용폭(~704px)을 거의 다 먹어
             품명이 ~26px 로 짜부라지고 한 글자씩 세로로 인쇄됐다. 인쇄에서만 비율(%)로 재배분.
             table-layout:fixed 는 유지(페이지별로 표가 쪼개지므로 열이 어긋나면 안 됨).
             th 인라인 width 를 이기려면 !important 필요. 헤더는 좁은 칸에서 줄바꿈 허용. */
          .cq-itemtable th{white-space:normal!important;font-size:10px!important;padding:5px 4px!important}
          .cq-itemtable td{padding:6px 4px!important}
          .cq-itemtable th:nth-child(1){width:4%!important}
          .cq-itemtable th:nth-child(2){width:25%!important}
          .cq-itemtable th:nth-child(3){width:11%!important}
          .cq-itemtable th:nth-child(4){width:9%!important}
          .cq-itemtable th:nth-child(5){width:12%!important}
          .cq-itemtable th:nth-child(6){width:9%!important}
          .cq-itemtable th:nth-child(7){width:9%!important}
          .cq-itemtable th:nth-child(8){width:11%!important}
          .cq-itemtable th:nth-child(9){width:10%!important}
          .cq-page{break-after:page}
          .cq-page:last-of-type{break-after:auto}
          .cq-pagehead{margin-bottom:10px}
          table thead{display:table-header-group}
          .cq-row,.cq-row td{break-inside:avoid}
          .cq-bottom{break-inside:avoid}
        }`}
      </style>
    </div>
  );
};
